// Kamera over kortet: km til skærm-px. Panorering med inerti, zoom omkring et punkt og pinch.
// I højformat kan kortet strækkes lidt på højden (`ky`), så et bredt net fylder en høj skærm i
// stedet for at ligge som en stribe midt i et hav af ingenting. Byer og figurer strækkes ikke,
// kun afstandene mellem dem.

import type { P } from './geo';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Et kamerabillede: skala, lodret stræk og forskydning. */
export interface CamView {
  scale: number;
  ky: number;
  ox: number;
  oy: number;
}

/** Højst så langt ind: så er der altid et par noder på skærmen, og nettet forsvinder aldrig helt. */
export const MAX_ZOOM = 2.5;

/**
 * Store skærme (1920x1080 og op) må gå 20 % længere ind. IGN-løkke 2, runde 3: "1920x1080 føles
 * underzoomet: små noder og navne med brede tomme marginer". Bane 1 ramte loftet på 2,5 med to noder.
 */
export const BIG_ZOOM = 3;

/** Højst så meget lodret stræk: mere end det, og Jylland ligner ikke sig selv. */
export const MAX_STRETCH = 1.45;

/** Telefon på højkant (Spor: telefonkort): skærmen er så høj, at et bredt net ellers står i en stribe. */
export const PHONE_STRETCH = 1.8;

export class Camera {
  scale = 1;
  ky = 1;
  ox = 0;
  oy = 0;
  /** Skalaen hvor hele banen passer i det frie felt. */
  fitScale = 1;
  /** Hvor langt ind (gange hele banen) kameraet må gå her: MAX_ZOOM, eller BIG_ZOOM på en stor skærm. */
  zoomCap = MAX_ZOOM;
  /** Har spilleren selv flyttet eller zoomet siden banen startede? Så rører startbilledet ikke kameraet. */
  moved = false;
  /** Hvornår spilleren sidst panorerede eller zoomede (performance.now). */
  movedAt = -Infinity;
  private vx = 0;
  private vy = 0;
  private bounds: Rect = { x: 0, y: 0, w: 1, h: 1 };
  private safe: Rect = { x: 0, y: 0, w: 1, h: 1 };
  /** Udgangsbilledet (hele banen) og en igangværende glidning. */
  private home: CamView = { scale: 1, ky: 1, ox: 0, oy: 0 };
  private glide: { from: CamView; to: CamView; t: number; ms: number } | null = null;

  /**
   * Passer hele `bounds` (km) ind i `safe` (px), med højst `maxStretch` lodret stræk. `minStretch`
   * under 1 klemmer højden, så et højt net bliver bredere i et lavt felt (telefon på siden).
   */
  fit(bounds: Rect, safe: Rect, maxStretch = 1, minStretch = 1): void {
    this.bounds = bounds;
    this.safe = safe;
    const sx = safe.w / bounds.w;
    const sy = safe.h / bounds.h;
    this.scale = Math.min(sx, sy / minStretch);
    this.ky = Math.max(minStretch, Math.min(maxStretch, sy / this.scale));
    this.fitScale = this.scale;
    this.ox = safe.x + (safe.w - bounds.w * this.scale) / 2 - bounds.x * this.scale;
    this.oy = safe.y + (safe.h - bounds.h * this.scale * this.ky) / 2 - bounds.y * this.scale * this.ky;
    this.home = this.view();
    this.glide = null;
    this.vx = 0;
    this.vy = 0;
  }

  view(): CamView {
    return { scale: this.scale, ky: this.ky, ox: this.ox, oy: this.oy };
  }

  set(v: CamView): void {
    this.scale = v.scale;
    this.ky = v.ky;
    this.ox = v.ox;
    this.oy = v.oy;
    this.glide = null;
  }

  get homeView(): CamView {
    return { ...this.home };
  }

  /**
   * Billedet, der viser `r` (km) i `area` (px) med `margin` px luft: zoom mellem `minZoom` og
   * `maxZoom` gange hele banen, og lodret stræk mellem `minStretch` og `maxStretch`.
   */
  frame(r: Rect, area: Rect, o: { maxZoom: number; minZoom?: number; maxStretch?: number; minStretch?: number; margin: number }): CamView {
    const m = o.margin;
    const lo = o.minStretch ?? 1;
    const sx = Math.max(1, area.w - m * 2) / Math.max(1, r.w);
    const sy = Math.max(1, area.h - m * 2) / Math.max(1, r.h);
    const scale = Math.max(this.fitScale * (o.minZoom ?? 1), Math.min(this.fitScale * o.maxZoom, sx, sy / lo));
    const ky = Math.max(lo, Math.min(o.maxStretch ?? 1, sy / scale));
    return {
      scale,
      ky,
      ox: area.x + area.w / 2 - (r.x + r.w / 2) * scale,
      oy: area.y + area.h / 2 - (r.y + r.h / 2) * scale * ky,
    };
  }

  /** Glid blødt hen til et billede. */
  glideTo(v: CamView, ms = 420): void {
    this.vx = 0;
    this.vy = 0;
    const c = this.view();
    if (Math.abs(c.scale - v.scale) < 1e-6 && Math.abs(c.ky - v.ky) < 1e-4 && Math.abs(c.ox - v.ox) < 0.5 && Math.abs(c.oy - v.oy) < 0.5) {
      this.glide = null;
      return;
    }
    this.glide = { from: c, to: { ...v }, t: 0, ms };
  }

  /** Står kameraet (næsten) i udgangsbilledet? */
  get atHome(): boolean {
    const h = this.home;
    return Math.abs(this.scale / h.scale - 1) < 0.04 && Math.abs(this.ky / h.ky - 1) < 0.04 && Math.abs(this.ox - h.ox) < 24 && Math.abs(this.oy - h.oy) < 24;
  }

  /** Glid tilbage til hele banen. */
  recenter(): void {
    this.moved = true;
    this.glideTo(this.home, 320);
  }

  /** Størrelsesfaktor for figurer: de vokser lidt, når man zoomer ind, men ikke lineært. */
  get k(): number {
    return Math.max(0.8, Math.min(1.7, Math.sqrt(this.scale / this.fitScale)));
  }

  toScreen(p: P): P {
    return { x: p.x * this.scale + this.ox, y: p.y * this.scale * this.ky + this.oy };
  }

  /** Skærm til km i udgangsbilledet (hele banen), uanset hvor kameraet står nu. */
  homeToWorld(s: P): P {
    const h = this.home;
    return { x: (s.x - h.ox) / h.scale, y: (s.y - h.oy) / (h.scale * h.ky) };
  }

  toWorld(s: P): P {
    return { x: (s.x - this.ox) / this.scale, y: (s.y - this.oy) / (this.scale * this.ky) };
  }

  panBy(dx: number, dy: number): void {
    this.glide = null;
    this.moved = true;
    this.movedAt = performance.now();
    this.ox += dx;
    this.oy += dy;
    this.clamp();
  }

  zoomAt(sx: number, sy: number, factor: number): void {
    this.glide = null;
    this.moved = true;
    this.movedAt = performance.now();
    // Aldrig længere ud end hele banen: ellers står kortet som en lille ø i et hav af ingenting.
    const next = Math.max(this.fitScale, Math.min(this.fitScale * Math.max(this.zoomCap, this.scale / this.fitScale), this.scale * factor));
    const f = next / this.scale;
    this.ox = sx - (sx - this.ox) * f;
    this.oy = sy - (sy - this.oy) * f;
    this.scale = next;
    this.clamp();
  }

  /** Slip efter panorering: fart i px/ms. */
  release(vx: number, vy: number): void {
    this.vx = Math.max(-3, Math.min(3, vx));
    this.vy = Math.max(-3, Math.min(3, vy));
  }

  stop(): void {
    this.vx = 0;
    this.vy = 0;
  }

  /** Billedet, kameraet ender i: målet for et glid, ellers det nuværende. */
  get destination(): CamView {
    return this.glide ? { ...this.glide.to } : this.view();
  }

  /** Glider kameraet lige nu? */
  get gliding(): boolean {
    return this.glide !== null;
  }

  update(dtMs: number): void {
    if (this.glide) {
      const g = this.glide;
      g.t = Math.min(1, g.t + dtMs / g.ms);
      const e = 1 - Math.pow(1 - g.t, 3);
      const a = g.from;
      const b = g.to;
      this.scale = a.scale + (b.scale - a.scale) * e;
      this.ky = a.ky + (b.ky - a.ky) * e;
      this.ox = a.ox + (b.ox - a.ox) * e;
      this.oy = a.oy + (b.oy - a.oy) * e;
      if (g.t >= 1) this.glide = null;
      return;
    }
    if (Math.abs(this.vx) < 0.01 && Math.abs(this.vy) < 0.01) return;
    this.ox += this.vx * dtMs;
    this.oy += this.vy * dtMs;
    const damp = Math.pow(0.992, dtMs);
    this.vx *= damp;
    this.vy *= damp;
    this.clamp();
  }

  /**
   * Banen fylder altid det frie felt: er den bredere end feltet, kan kanten ikke trækkes ind over
   * feltets kant (plus lidt luft); er den smallere, står den i midten. Samme for højden.
   */
  private clamp(): void {
    const b = this.bounds;
    const s = this.safe;
    const slack = 40;
    const axis = (o: number, lo: number, len: number, b0: number, bLen: number, sc: number): number => {
      const w = bLen * sc;
      if (w <= len) {
        // Lidt fri bevægelse, men midten bliver i feltet.
        const centred = lo + (len - w) / 2 - b0 * sc;
        return Math.max(centred - slack, Math.min(centred + slack, o));
      }
      const max = lo + slack - b0 * sc;
      const min = lo + len - slack - (b0 + bLen) * sc;
      return Math.max(min, Math.min(max, o));
    };
    this.ox = axis(this.ox, s.x, s.w, b.x, b.w, this.scale);
    this.oy = axis(this.oy, s.y, s.h, b.y, b.h, this.scale * this.ky);
  }
}
